import { useState, useEffect, useCallback, useMemo } from 'react';

import { FiscalYearsRes, FiscalYear, Space, SelectOption } from 'models';
import { BalanceType } from 'utils/enums';

import { useStateSelector } from 'hooks';
import services from 'services';

import { getCurrentCooperative } from 'store/home/selectors';

import { toDate, getBalanceEndDate, filterBalanceFiscalYears } from 'utils';
import format from 'date-fns/format';

import { alphanumericSort } from 'shared/utils/sorting';

import { BalanceDetails } from 'components';

function extractSpace(spaces: Space[] | undefined) {
  return spaces && spaces.length ? spaces[0] : null;
}

function extractCurrentFiscalYear(fiscalYears: FiscalYear[] | undefined) {
  if (!fiscalYears || !fiscalYears.length) {
    return null;
  }

  return fiscalYears[0];
}

function sortSpaces(spaces: Space[]) {
  return [...spaces].sort((a, b) =>
    alphanumericSort(a.Name ?? '', b.Name ?? '')
  );
}

interface BalanceState {
  spaces: Space[];
  selectedSpace: Space | null;
  fiscalYearsData: FiscalYearsRes;
  selectedFiscalYears: FiscalYear | null;
  startDate: Date | null;
  endDate: Date | null;
  loading: boolean;
  error: boolean;
  balanceType: SelectOption<BalanceType>;
}

const Balance = () => {
  const { currentCooperative, coopName } = useStateSelector((state) => ({
    currentCooperative: state.settings.current_cooperative,
    coopName: getCurrentCooperative(state)?.Name || null,
  }));
  const [state, setState] = useState<BalanceState>({
    spaces: [],
    fiscalYearsData: {
      ClosedPeriodEnd: null,
      FiscalYears: [],
      NotMappedPaymentDate: null,
    },
    startDate: null,
    endDate: null,
    selectedFiscalYears: null,
    selectedSpace: null,
    loading: false,
    error: false,
    balanceType: { label: 'Kaikki', value: BalanceType.All },
  });

  useEffect(() => {
    if (currentCooperative) {
      fetchData(currentCooperative);
    }

    /* eslint-disable-next-line */
  }, [currentCooperative]);

  const fetchFiscalYears = async (coopId: string) => {
    try {
      const res: FiscalYearsRes = (
        await services.get(`/api/cooperatives/${coopId}/fiscalyears`)
      ).data.Data;

      return res;
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSpaces = async (coopId: string) => {
    try {
      const spaces: Space[] = (
        await services.get(
          `/api/user/report-balance/cooperatives/${coopId}/spaces`
        )
      ).data.Data;

      return sortSpaces(spaces);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchData = async (coopId: string) => {
    setState((prevState) => ({ ...prevState, loading: true, error: false }));

    try {
      const fiscalYearsData = await fetchFiscalYears(coopId);
      const spaces = await fetchSpaces(coopId);

      const currentSpace = extractSpace(spaces);
      const fiscalYears = filterBalanceFiscalYears(fiscalYearsData, new Date());
      const currentFiscalYear = extractCurrentFiscalYear(fiscalYears);
      const startDate = toDate(currentFiscalYear?.StartDate);
      const endDate = toDate(currentFiscalYear?.EndDate);

      setState((prevState) => ({
        ...prevState,
        spaces: spaces || [],
        fiscalYearsData: {
          ClosedPeriodEnd: fiscalYearsData?.ClosedPeriodEnd || null,
          FiscalYears: fiscalYears,
          NotMappedPaymentDate: fiscalYearsData?.NotMappedPaymentDate || null,
        },
        selectedSpace: currentSpace,
        selectedFiscalYears: currentFiscalYear,
        startDate,
        endDate: endDate ? getBalanceEndDate(endDate) : null,
        loading: false,
        error: false,
      }));
    } catch (err) {
      console.error(err);
      setState((prevState) => ({ ...prevState, loading: false, error: true }));
    }
  };

  const handleChangeFiscalYear = useCallback((fiscalYear: FiscalYear) => {
    setState((prevState) => {
      const endDate = toDate(fiscalYear.EndDate);
      return {
        ...prevState,
        selectedFiscalYears: fiscalYear,
        startDate: toDate(fiscalYear.StartDate),
        endDate: endDate ? getBalanceEndDate(endDate) : null,
      };
    });
  }, []);

  const handleChangeSpace = useCallback((space: Space) => {
    setState((prevState) => ({
      ...prevState,
      selectedSpace: space,
    }));
  }, []);

  const handleChangeStartDate = useCallback((date: Date | null) => {
    setState((prevState) => ({
      ...prevState,
      startDate: date,
    }));
  }, []);

  const handleChangeEndDate = useCallback((date: Date | null) => {
    setState((prevState) => ({
      ...prevState,
      endDate: date,
    }));
  }, []);

  const handleChangeBalanceType = useCallback(
    (option: SelectOption<BalanceType>) => {
      setState((prevState) => ({
        ...prevState,
        balanceType: option,
      }));
    },
    []
  );

  const minDate = useMemo(() => {
    return toDate(state.selectedFiscalYears?.StartDate);
  }, [state.selectedFiscalYears?.StartDate]);

  const maxDate = useMemo(() => {
    const endDate = toDate(state.selectedFiscalYears?.EndDate);

    return endDate ? getBalanceEndDate(endDate) : null;
  }, [state.selectedFiscalYears]);

  const fileName = useMemo(() => {
    const startDate = state.startDate
      ? format(state.startDate, 'd.M.yyyy')
      : '';
    const endDate = state.endDate ? format(state.endDate, 'd.M.yyyy') : '';

    return `balance__${coopName}__${startDate} - ${endDate}`;
  }, [coopName, state.startDate, state.endDate]);

  return (
    <BalanceDetails
      {...state}
      fileName={fileName}
      minDate={minDate}
      maxDate={maxDate}
      onChangeFiscalYear={handleChangeFiscalYear}
      onChangeStartDate={handleChangeStartDate}
      onChangeEndDate={handleChangeEndDate}
      onChangeSpace={handleChangeSpace}
      onChangeBalanceType={handleChangeBalanceType}
    />
  );
};

export default Balance;
